import type { ApexOptions } from "apexcharts"
import { merge } from "lodash"

export enum ApexChartType {
  line = "line",
  bar = "bar",
  donut = "donut",
}

const DAY_MS = 24 * 60 * 60 * 1000

const clampZoomToDay = (_chartContext: any, { xaxis }: any) => {
  const rawMin = xaxis?.min
  const rawMax = xaxis?.max

  if (rawMin == null || rawMax == null) {
    return undefined
  }

  let min = new Date(rawMin)
  let max = new Date(rawMax)

  // snap to day boundaries
  min = new Date(min.getFullYear(), min.getMonth(), min.getDate(), 0, 0, 0, 0)
  max = new Date(max.getFullYear(), max.getMonth(), max.getDate(), 23, 59, 59, 999)

  const snappedMin = min.getTime()
  let snappedMax = max.getTime()

  // never allow zooming smaller than one full day
  if (snappedMax - snappedMin < DAY_MS) {
    snappedMax = snappedMin + DAY_MS - 1
  }

  return {
    xaxis: {
      min: snappedMin,
      max: snappedMax,
    },
  }
}

const DefaultApexLineOptions: ApexOptions = {
  chart: {
    toolbar: { show: false },
    height: 285,
    type: "line",
    // Animations off. A dashboard mounts several of these at once, each with a
    // point per day per network, and animating them all on mount and on every
    // filter change is where the page's stutter came from.
    animations: { enabled: false },
    zoom: {
      enabled: false,
      type: "x",
      autoScaleYaxis: true,
    },
    events: {
      beforeZoom: clampZoomToDay,
      zoomed: clampZoomToDay,
      scrolled: clampZoomToDay,
    },
    // dropShadow deliberately off: an SVG blur filter over a many-point path is
    // expensive to rasterise and costs more than the depth cue is worth.
    dropShadow: { enabled: false },
    stacked: true,
  },
  grid: { borderColor: "#f1f1f1" },
  dataLabels: { enabled: false },
  stroke: { width: 2, curve: "smooth", lineCap: "butt", dashArray: 0 },
  title: { text: undefined },
  legend: {
    show: true,
    position: "top",
    horizontalAlign: "center",
    fontWeight: 600,
    fontSize: "11px",
    tooltipHoverFormatter: function (val: any, opts: any) {
      return (
          val +
          " - " +
          opts.w.globals.series[opts.seriesIndex][opts.dataPointIndex] +
          ""
      )
    },
    labels: { colors: "#74767c" },
    markers: {
      strokeWidth: 0,
      offsetX: 0,
      offsetY: 0,
    },
    formatter: (label) => {
      return titleCase(label)
    },
  },
  yaxis: {
    title: {
      style: {
        color: "#adb5be",
        fontSize: "14px",
        fontFamily: "poppins, sans-serif",
        fontWeight: 600,
        cssClass: "apexcharts-yaxis-label",
      },
    },
    labels: {
      formatter: (value: any) => `$${value.toFixed(0)}`,
      show: true,
      style: {
        colors: "#8c9097",
        fontSize: "11px",
        fontWeight: 600,
        cssClass: "apexcharts-xaxis-label",
      },
    },
  },
  xaxis: {
    type: "datetime",
    tickAmount: 6,
    axisBorder: {
      show: true,
      color: "rgba(119, 119, 142, 0.05)",
      offsetX: 0,
      offsetY: 0,
    },
    axisTicks: {
      show: true,
      borderType: "solid",
      color: "rgba(119, 119, 142, 0.05)",
      offsetX: 0,
      offsetY: 0,
    },
    labels: {
      rotate: -90,
      datetimeFormatter: {
        year: "yyyy",
        month: "MMM dd",
        day: "MMM dd",
        hour: "MMM dd", // never show hour labels
        minute: "MMM dd",
      },
      style: {
        colors: "#8c9097",
        fontSize: "11px",
        fontWeight: 600,
        cssClass: "apexcharts-xaxis-label",
      },
    },
  },
  tooltip: {
    x: {
      format: "MMM dd, yyyy",
    },
    y: [
      { title: { formatter: (val: any) => val } },
      { title: { formatter: (val: any) => val } },
      { title: { formatter: (val: any) => val } },
    ],
  },
  colors: ["var(--primary04)"],
}

const DefaultApexBarOptions: ApexOptions = {
  chart: {
    animations: { enabled: false },
    toolbar: {
      show: false,
    },
    stacked: true,
    type: "bar",
    height: "auto",
    zoom: {
      enabled: false,
      type: "x",
      autoScaleYaxis: true,
    },
    events: {
      beforeZoom: clampZoomToDay,
      zoomed: clampZoomToDay,
      scrolled: clampZoomToDay,
    },
  },
  grid: {
    borderColor: "#f5f4f4",
    strokeDashArray: 5,
  },
  colors: ["var(--primary04)"],

  plotOptions: {
    bar: {
      colors: {
        ranges: [
          {
            from: -100,
            to: -46,
            color: "#ebeff5",
          },
          {
            from: -45,
            to: 0,
            color: "#ebeff5",
          },
        ],
      },
      columnWidth: "20%",
    },
  },
  dataLabels: {
    enabled: false,
  },
  legend: {
    show: true,
    position: "top",
    formatter: (label) => {
      return titleCase(label)
    },
  },
  yaxis: {
    labels: {
      formatter: function (y) {
        return y.toFixed(0) + ""
      },
    },
  },
  xaxis: {
    type: "datetime",
    axisBorder: {
      show: false,
      color: "rgba(119, 119, 142, 0.05)",
      offsetX: 0,
      offsetY: 0,
    },
    axisTicks: {
      show: false,
      borderType: "solid",
      color: "rgba(119, 119, 142, 0.05)",
      offsetX: 0,
      offsetY: 0,
    },
    labels: {
      rotate: -90,
      datetimeFormatter: {
        year: "yyyy",
        month: "MMM dd",
        day: "MMM dd",
        hour: "MMM dd",
        minute: "MMM dd",
      },
    },
  },
  tooltip: {
    x: {
      format: "MMM dd, yyyy",
    },
  },
}

const DefaultApexDonutOptions: ApexOptions = {
  labels: ["Meta", "Snap", "Pinterest", "Google"],
  chart: {
    events: {
      mounted: (chart: any) => {
        chart.windowResizeHandler()
      },
    },
    height: 250,
    type: "donut",
  },
  dataLabels: {
    enabled: false,
  },

  legend: {
    show: false,
  },
  stroke: {
    show: true,
    curve: "smooth",
    lineCap: "round",
    colors: ["#fff"],
    width: 0,
    dashArray: 0,
  },
  plotOptions: {
    pie: {
      expandOnClick: false,
      donut: {
        size: "82%",
        labels: {
          show: false,
          name: {
            show: true,
            fontSize: "20px",
            color: "#495057",
            offsetY: -4,
          },
          value: {
            show: true,
            fontSize: "18px",
            color: undefined,
            offsetY: 8,
            formatter: function (val) {
              return val + "%"
            },
          },
        },
      },
    },
  },
  colors: ["#0064e0", "#FFFC00", "#c8232c", "#34A853"],
}

const DefaultApexOptionsByType: Record<ApexChartType, ApexOptions> = {
  [ApexChartType.line]: DefaultApexLineOptions,
  [ApexChartType.bar]: DefaultApexBarOptions,
  [ApexChartType.donut]: DefaultApexDonutOptions,
}

export const makeApexOptions = (
  overrides: ApexOptions = {},
  chartType: ApexChartType = ApexChartType.line,
): ApexOptions => {
  const base = DefaultApexOptionsByType[chartType]

  return merge({}, base, overrides)
}

const titleCase = (label: string) => {
  return label
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}